interface SwappedStereoRoute {
  source: MediaElementAudioSourceNode
  nodes: AudioNode[]
}

let sharedAudioContext: AudioContext | null = null
const routes = new WeakMap<HTMLMediaElement, SwappedStereoRoute>()

function getAudioContext(): AudioContext | null {
  const AudioContextConstructor =
    window.AudioContext ?? window.webkitAudioContext
  if (typeof AudioContextConstructor !== "function") {
    return null
  }
  sharedAudioContext ??= new AudioContextConstructor()
  return sharedAudioContext
}

function routeSwappedStereo(
  audioContext: AudioContext,
  media: HTMLMediaElement,
): SwappedStereoRoute {
  const source = audioContext.createMediaElementSource(media)

  // A splitter reads its input discretely, so mono would reach only one ear; up-mixing to stereo first copies a mono signal into both channels.
  const upmix = audioContext.createGain()
  upmix.channelCount = 2
  upmix.channelCountMode = "explicit"
  upmix.channelInterpretation = "speakers"

  const splitter = audioContext.createChannelSplitter(2)
  const merger = audioContext.createChannelMerger(2)

  source.connect(upmix)
  upmix.connect(splitter)
  splitter.connect(merger, 0, 1)
  splitter.connect(merger, 1, 0)
  merger.connect(audioContext.destination)

  return { source, nodes: [upmix, splitter, merger] }
}

/**
 * Plays `media` with its stereo channels crossed. Recordings carry the agent on the left and the persona on the right (`AGENT_CHANNEL_INDEX` in `src/shared/components/audio-player/useAudioWaveformOptions.ts`), while the transcript shows the persona on the left and the agent on the right, so crossing them puts each speaker on the side the transcript shows. Mono audio plays in both ears.
 *
 * The element is routed into Web Audio here, before it plays. Moving a playing element from its native output into Web Audio makes Chrome switch audio renderers mid-stream: the first second plays uncrossed, then `currentTime` stalls for about half a second while the switch lands. The context starts suspended until a user gesture, so playback resumes it.
 *
 * Call this before the element's source is assigned: `crossOrigin` applies only to loads that start after it is set, and Web Audio outputs silence for cross-origin media loaded without CORS.
 *
 * @returns a cleanup that removes the listener and disconnects the route. An element can enter Web Audio only once and plays silently after its route is disconnected, so call it when the element is discarded.
 */
export function attachSwappedStereoPlayback(
  media: HTMLMediaElement,
): () => void {
  media.crossOrigin = "anonymous"

  const audioContext = getAudioContext()
  if (!audioContext) {
    return () => {}
  }

  if (!routes.has(media)) {
    try {
      routes.set(media, routeSwappedStereo(audioContext, media))
    } catch {
      // The element already belongs to another AudioContext; it stays on its native output.
      return () => {}
    }
  }

  const handlePlay = () => {
    if (audioContext.state === "suspended") {
      void audioContext.resume()
    }
  }

  media.addEventListener("play", handlePlay)

  return () => {
    media.removeEventListener("play", handlePlay)
    const route = routes.get(media)
    if (!route) {
      return
    }
    route.source.disconnect()
    route.nodes.forEach((node) => node.disconnect())
    routes.delete(media)
  }
}
